/**
 * Alles, was Rangliste und Tipp-Vergleich über eine CL-Runde wissen müssen —
 * einmal geladen, von beiden gelesen. Gegenstück zu useLeagueTipData.
 *
 * Enthält bewusst auch die Live-Tabelle der Ligaphase. Sie fehlte der CL-Seite
 * bisher ganz: App.tsx überspringt den Tabellen-Abruf für CUP, dadurch bekam
 * die Wertung nie eine echte Reihenfolge und stand für alle auf 0.
 */

import { useEffect, useState } from "react";
import { tournamentService, type TournamentTipWithProfile } from "../services/TournamentService.ts";
import { competitionPhaseService } from "../services/CompetitionPhaseService.ts";
import { specialQuestionService } from "../services/SpecialQuestionService.ts";
import { leagueService } from "../services/LeagueService.ts";
import type {
  EffectivePhase,
  Fixture,
  SpecialQuestion,
  SpecialQuestionAnswer,
  SpecialQuestionOption,
  Standing,
} from "../types/index.ts";

export interface CupTipData {
  tips: TournamentTipWithProfile[];
  fixtures: Fixture[];
  phases: EffectivePhase[];
  /** Die 36 Teams der Ligaphase in ihrer aktuellen Reihenfolge. */
  standings: Standing[];
  questions: SpecialQuestion[];
  options: SpecialQuestionOption[];
  answers: SpecialQuestionAnswer[];
  lockedQuestionIds: number[];
}

const EMPTY: CupTipData = {
  tips: [],
  fixtures: [],
  phases: [],
  standings: [],
  questions: [],
  options: [],
  answers: [],
  lockedQuestionIds: [],
};

const cache = new Map<string, Promise<CupTipData>>();

const keyOf = (competitionId: number, tipRoundId: number, code: string) =>
  `${competitionId}:${tipRoundId}:${code}`;

async function load(
  competitionId: number,
  tipRoundId: number,
  competitionCode: string,
): Promise<CupTipData> {
  const [tips, fixtures, phases, standings, sq] = await Promise.all([
    tournamentService.getAllTipsWithProfiles(competitionId, tipRoundId),
    tournamentService.fetchFixtures(competitionId),
    competitionPhaseService.getEnrichedPhases(competitionId),
    // Ohne Tabelle bleibt die Wertung bei 0 — aber ein Ausfall der Quelle darf
    // nicht die ganze Seite mitnehmen.
    leagueService.fetchStandings(competitionCode).catch(() => [] as Standing[]),
    specialQuestionService.listLockedAnswersForRound(tipRoundId, competitionId),
  ]);
  return {
    tips,
    fixtures,
    phases,
    standings,
    questions: sq.questions,
    options: sq.options,
    answers: sq.answers,
    lockedQuestionIds: sq.lockedQuestionIds,
  };
}

export function invalidateCupTipData(
  competitionId: number,
  tipRoundId: number,
  competitionCode: string,
): void {
  cache.delete(keyOf(competitionId, tipRoundId, competitionCode));
}

export function useCupTipData(
  competitionId: number | undefined,
  tipRoundId: number | undefined,
  competitionCode: string | undefined,
): { data: CupTipData; loading: boolean } {
  const [data, setData] = useState<CupTipData>(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!competitionId || !tipRoundId || !competitionCode) {
      setData(EMPTY);
      setLoading(false);
      return;
    }
    const key = keyOf(competitionId, tipRoundId, competitionCode);
    let pending = cache.get(key);
    if (!pending) {
      pending = load(competitionId, tipRoundId, competitionCode).catch((e) => {
        cache.delete(key);
        console.error("useCupTipData failed:", e);
        return EMPTY;
      });
      cache.set(key, pending);
    }

    let cancelled = false;
    setLoading(true);
    void pending.then((result) => {
      if (cancelled) return;
      setData(result);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [competitionId, tipRoundId, competitionCode]);

  return { data, loading };
}
